// formatActivityFieldChange.js

import {
  getProfilePeople,
  getProfilePersonName,
} from 'profile/ProfileFieldValueUtils';
import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import {
  getEnumOptionLabel,
  resolveEnumOptionsForField,
} from 'utilities/EnumOptions';
import {
  isEnumCompatibleFieldType,
  normalizeSchemaFieldType,
} from 'utilities/FieldTypes';
import { formatLocationDisplayValue } from 'utilities/Location';
import { formatMeasurementValue } from 'utilities/Measurement';
import { getResolvedFields } from 'utilities/NodeType';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { buildRichTextSummary, extractRichTextPlainText } from 'utilities/RichText';
import { formatSignatureFieldDisplay } from 'utilities/SignatureFieldValue';
import {
  formatTemporalFieldValue,
  isTemporalFieldType,
} from 'views/utils/temporalFieldFormat';

/** Soft cap so activity lines stay scannable. */
const MAX_DISPLAY_LENGTH = 80;

const CORE_FIELD_DEFS = Object.freeze({
  title: { name: 'title', type: 'string', label: 'Title' },
  description: { name: 'description', type: 'richtext', label: 'Description' },
  tags: { name: 'tags', type: 'tags', label: 'Tags' },
  coverUrl: { name: 'coverUrl', type: 'cover', label: 'Cover' },
});

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isEmptyActivityValue(value) {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return !Number.isFinite(value);
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isSanitizerPlaceholder(value) {
  return value === '[Object]' || value === '[Array]';
}

/**
 * @param {string} text
 * @param {number} [maxLength]
 * @returns {string}
 */
function truncateActivityDisplay(text, maxLength = MAX_DISPLAY_LENGTH) {
  const normalized = String(text || '').trim();
  if (!normalized) return '';
  if (normalized.length <= maxLength) return normalized;
  const softLimit = Math.max(1, maxLength - 1);
  const wordBoundary = normalized.lastIndexOf(' ', softLimit);
  const cutoff = wordBoundary >= Math.floor(softLimit * 0.6)
    ? wordBoundary
    : softLimit;
  return `${normalized.slice(0, cutoff).trimEnd()}…`;
}

/**
 * @param {unknown} value
 * @param {object|null|undefined} fieldDef
 * @returns {string}
 */
function formatEnumCompatibleValue(value, fieldDef) {
  const options = resolveEnumOptionsForField(fieldDef);
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return '';
  return getEnumOptionLabel(options, raw) || raw;
}

/**
 * @param {unknown} value
 * @param {object|null|undefined} fieldDef
 * @returns {string}
 */
function formatMultiselectValue(value, fieldDef) {
  const options = resolveEnumOptionsForField(fieldDef);
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => {
      const raw = entry == null ? '' : String(entry).trim();
      if (!raw) return '';
      return getEnumOptionLabel(options, raw) || raw;
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatPeopleValue(value) {
  const people = getProfilePeople(value);
  if (people.length) {
    return people.map((person) => getProfilePersonName(person)).filter(Boolean).join(', ');
  }

  // Legacy / alternate shapes that still carry a display name.
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  return list
    .map((entry) => {
      if (entry == null) return '';
      if (typeof entry === 'string') return entry.trim();
      if (typeof entry !== 'object') return '';
      return String(
        entry.name
        || entry.title
        || entry.email
        || entry.displayName
        || ''
      ).trim();
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatUrlValue(value) {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim();
  }
  if (value && typeof value === 'object') {
    const title = String(value.title || '').trim();
    const url = String(value.url || '').trim();
    if (title && url) return `${title} — ${url}`;
    return title || url || '';
  }
  return '';
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatChecklistValue(value) {
  const totalItems = Number(value?.totalItems) || 0;
  const completedItems = Number(value?.completedItems) || 0;
  if (totalItems > 0) return `${completedItems}/${totalItems}`;

  if (Array.isArray(value)) {
    if (!value.length) return '';
    const doneCount = value.filter(
      (item) => item?.completed || item?.checked || item?.status === 'done'
    ).length;
    return `${doneCount}/${value.length}`;
  }

  return '';
}

/**
 * Format a stored activity value for inline copy.
 * Returns '' when the value cannot be shown helpfully (caller falls back to "updated").
 *
 * @param {unknown} value
 * @param {object|null|undefined} fieldDef
 * @param {string} [fieldPath]
 * @returns {string}
 */
export function formatActivityFieldDisplayValue(value, fieldDef = null, fieldPath = '') {
  if (isSanitizerPlaceholder(value)) return '';
  if (isEmptyActivityValue(value)) return '';

  const pathKey = String(fieldPath || '').split('.').pop() || '';
  const effectiveDef = fieldDef || CORE_FIELD_DEFS[pathKey] || null;
  const fieldType = normalizeSchemaFieldType(
    effectiveDef?.type
    || (pathKey === 'tags' ? 'tags' : '')
    || (pathKey === 'coverUrl' ? 'cover' : '')
  );

  if (fieldType === 'cover') {
    // URLs are noisy in the feed; presence alone is enough.
    return 'an image';
  }

  if (fieldType === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (fieldType === 'money' || (fieldType === 'currency' && effectiveDef?.currencyCode)) {
    return formatPrice(value, resolveFieldCurrencyCode(effectiveDef)) || '';
  }

  if (fieldType === 'currency') {
    return String(value || '').trim().toUpperCase();
  }

  if (fieldType === 'percentage') {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    return `${Math.max(0, Math.min(100, Math.round(numeric)))}%`;
  }

  if (fieldType === 'measurement') {
    return formatMeasurementValue(value) || '';
  }

  if (fieldType === 'location') {
    return formatLocationDisplayValue(value) || '';
  }

  if (isEnumCompatibleFieldType(fieldType)) {
    return formatEnumCompatibleValue(value, effectiveDef);
  }

  if (fieldType === 'multiselect') {
    return formatMultiselectValue(value, effectiveDef);
  }

  if (fieldType === 'people') {
    return formatPeopleValue(value);
  }

  if (fieldType === 'relation') {
    // Field-change payloads usually store keys only — titles need hydration.
    const keys = normalizeNodelistKeys(value);
    if (!keys.length) return '';
    return '';
  }

  if (fieldType === 'richtext') {
    return buildRichTextSummary(value, MAX_DISPLAY_LENGTH)
      || extractRichTextPlainText(value)
      || '';
  }

  if (fieldType === 'signature') {
    return formatSignatureFieldDisplay(value) || '';
  }

  if (fieldType === 'url') {
    return formatUrlValue(value);
  }

  if (fieldType === 'tags') {
    return Array.isArray(value)
      ? value.map((entry) => String(entry || '').trim()).filter(Boolean).join(', ')
      : String(value);
  }

  if (fieldType === 'checklist') {
    return formatChecklistValue(value);
  }

  if (fieldType === 'color') {
    return String(value || '').trim();
  }

  if (isTemporalFieldType(fieldType) || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
    const temporalType = isTemporalFieldType(fieldType) ? fieldType : 'datetime';
    const formatted = formatTemporalFieldValue({
      value,
      fieldType: temporalType,
      locale: 'en-US',
      dateOptions: { month: 'short', day: 'numeric', year: 'numeric' },
      dateTimeOptions: {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      },
    });
    if (formatted) return formatted;
  }

  if (fieldType === 'number' || fieldType === 'sequence') {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && String(value).trim() !== '') return String(value).trim();
  }

  if (
    fieldType === 'string'
    || fieldType === 'subtitle'
    || fieldType === 'email'
    || fieldType === 'phone'
    || fieldType === 'barcode'
  ) {
    return truncateActivityDisplay(String(value));
  }

  // Heuristics when schema is unavailable.
  if (!fieldType) {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        const formatted = formatTemporalFieldValue({
          value,
          fieldType: 'datetime',
          locale: 'en-US',
          dateOptions: { month: 'short', day: 'numeric', year: 'numeric' },
          dateTimeOptions: {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          },
        });
        if (formatted) return formatted;
      }
      return truncateActivityDisplay(value);
    }
    if (Array.isArray(value)) {
      const people = formatPeopleValue(value);
      if (people) return people;
      if (value.every((entry) => typeof entry === 'string' || typeof entry === 'number')) {
        return truncateActivityDisplay(value.map((entry) => String(entry)).join(', '));
      }
      return '';
    }
    if (typeof value === 'object') {
      const location = formatLocationDisplayValue(value);
      if (location) return location;
      const richText = buildRichTextSummary(value, MAX_DISPLAY_LENGTH);
      if (richText) return richText;
      const signature = formatSignatureFieldDisplay(value);
      if (signature) return signature;
      const name = String(value.name || value.title || value.label || '').trim();
      if (name) return truncateActivityDisplay(name);
      return '';
    }
  }

  if (Array.isArray(value)) {
    if (value.every((entry) => typeof entry === 'string' || typeof entry === 'number')) {
      return truncateActivityDisplay(value.map((entry) => String(entry)).join(', '));
    }
    return '';
  }

  if (typeof value === 'object') return '';

  return truncateActivityDisplay(String(value));
}

/**
 * Continuation after the actor name ("Kevin …").
 *
 * @param {object} params
 * @param {unknown} params.fieldLabel
 * @param {unknown} [params.newValue]
 * @param {boolean} [params.hasNewValue] - Whether newValue was present on the change.
 * @param {object|null|undefined} [params.fieldDef]
 * @param {string} [params.fieldPath]
 * @returns {string}
 */
export function formatActivityFieldChangeMessage({
  fieldLabel,
  newValue,
  hasNewValue = false,
  fieldDef = null,
  fieldPath = '',
} = {}) {
  const label = String(fieldLabel || 'details').trim().toLowerCase() || 'details';

  if (!hasNewValue) {
    return `updated ${label}.`;
  }

  if (isEmptyActivityValue(newValue)) {
    return `cleared the ${label}.`;
  }

  const display = formatActivityFieldDisplayValue(newValue, fieldDef, fieldPath);
  if (!display) {
    return `updated ${label}.`;
  }

  return `set the ${label} to ${display}.`;
}

/**
 * @param {object|null|undefined} node
 * @returns {Record<string, object>}
 */
export function buildActivityFieldDefLookup(node) {
  const lookup = { ...CORE_FIELD_DEFS };
  if (!node?.type) return lookup;

  const fields = getResolvedFields(node.type, { includeHidden: true, excludeSecured: true });

  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (!field?.name || !field?.type) return;
    if (typeof field.type === 'string' && field.type.endsWith('_form')) return;

    const sourceKey = String(field.source || node.type).toLowerCase();
    const fieldDataKey = `data_${sourceKey}`;
    const fieldName = String(field.name).trim();
    if (!fieldName) return;

    lookup[`${fieldDataKey}.${fieldName}`] = field;
    // Unprefixed name as a soft fallback when path resolution loses the bag.
    if (!lookup[fieldName]) lookup[fieldName] = field;
  });

  return lookup;
}

/**
 * @param {Record<string, object>} fieldDefs
 * @param {unknown} path
 * @returns {object|null}
 */
export function resolveActivityFieldDef(fieldDefs, path) {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return null;
  if (fieldDefs?.[normalizedPath]) return fieldDefs[normalizedPath];

  const segments = normalizedPath.split('.');
  const leaf = segments[segments.length - 1];
  if (leaf && fieldDefs?.[leaf]) return fieldDefs[leaf];
  if (CORE_FIELD_DEFS[leaf]) return CORE_FIELD_DEFS[leaf];

  return null;
}
